import { useEffect, useMemo } from "react"

import { useLocation, useNavigate } from "react-router-dom"

import { useRoutes } from "@/contexts/use-routes"

import { useGetTrainingModules } from "./use-get-training-modules"
import { useGetTrainingProgress } from "./use-get-training-progress"
import { useTrainingLinks } from "./use-training-links"
import { useUpdateModuleStatus } from "./use-update-module-status"

export function useTrainingNavigation() {
  const navigate = useNavigate()
  const location = useLocation()
  const { trainingLinks } = useTrainingLinks()
  const { trainingTracks, trainingModules } = useRoutes()

  const trainingModulesQuery = useGetTrainingModules()
  const updateModuleStatusMutation = useUpdateModuleStatus()
  const progressQuery = useGetTrainingProgress()

  const isOnCoursePage = location.pathname !== "/training/" && location.pathname !== "/training"
  const onboardingSearch =
    new URLSearchParams(location.search).get("onboarding") === "1" ? location.search : ""

  // Determine which track the user is currently in
  const currentTrack = useMemo(() => {
    for (const track of trainingTracks) {
      const trackModuleIds = new Set(track.module_ids)
      for (const mod of trainingModules) {
        if (!trackModuleIds.has(mod.id)) continue
        for (const sec of mod.sections) {
          if (sec.url === location.pathname) return track
        }
      }
    }
    return null
  }, [trainingTracks, trainingModules, location.pathname])

  // Get links scoped to current track
  const trackLinks = useMemo(() => {
    if (!currentTrack) return trainingLinks
    const moduleIds = new Set(currentTrack.module_ids)
    return trainingLinks.filter((link) => moduleIds.has(link.id))
  }, [currentTrack, trainingLinks])

  const isOnQuizPage = useMemo(() => {
    if (!currentTrack) return false
    return location.pathname.includes("/quiz")
  }, [currentTrack, location.pathname])

  const isOnLastPage = isOnQuizPage

  const currentModule = trackLinks
    .flatMap((link) => link.sections)
    .find((section) => section.url === location.pathname)

  const isCurrentModuleCompleted =
    trainingModulesQuery.data?.modules.find((module) => module.module_id === currentModule?.id)
      ?.status === "completed"

  const findPosition = (currentPath: string) => {
    for (let linkIndex = 0; linkIndex < trackLinks.length; linkIndex++) {
      const itemIndex = trackLinks[linkIndex].sections.findIndex(
        (section) => section.url === currentPath
      )
      if (itemIndex !== -1) {
        return { linkIndex, itemIndex }
      }
    }
    return null
  }

  const getNextUrl = (currentPath: string) => {
    const position = findPosition(currentPath)
    if (!position) return ""
    const { linkIndex, itemIndex } = position
    const currentLink = trackLinks[linkIndex]
    if (itemIndex + 1 < currentLink.sections.length) {
      return currentLink.sections[itemIndex + 1].url
    }
    return linkIndex + 1 < trackLinks.length ? trackLinks[linkIndex + 1].sections[0].url : ""
  }

  const getPreviousUrl = (currentPath: string) => {
    const position = findPosition(currentPath)
    if (!position) return ""
    const { linkIndex, itemIndex } = position
    if (itemIndex > 0) {
      return trackLinks[linkIndex].sections[itemIndex - 1].url
    }
    if (linkIndex > 0) {
      const previousLink = trackLinks[linkIndex - 1]
      return previousLink.sections[previousLink.sections.length - 1].url
    }
    return ""
  }

  const handleNextClick = async () => {
    const nextUrl = getNextUrl(location.pathname)

    // Mark current section as completed
    if (!isCurrentModuleCompleted && currentModule) {
      await updateModuleStatusMutation.mutateAsync({
        moduleId: currentModule.id,
        data: { status: "completed", completed_at: new Date().toISOString() },
      })
    }

    if (isOnQuizPage || !nextUrl) {
      void navigate(`/training${onboardingSearch}`)
      return
    }

    void navigate(`${nextUrl}${onboardingSearch}`)
  }

  const handleBackClick = () => {
    const previousUrl = getPreviousUrl(location.pathname)
    if (previousUrl) {
      void navigate(`${previousUrl}${onboardingSearch}`)
    }
  }

  // Free navigation within tracks — only enforce track-level locking + mark progress
  useEffect(() => {
    if (!isOnCoursePage) return

    // Track locking: if required track is not completed, redirect to landing
    if (currentTrack?.requires_track) {
      if (!progressQuery.data) return // Wait for data before allowing access
      const requiredTrackProgress = progressQuery.data.tracks.find(
        (tp) => tp.trackId === currentTrack.requires_track
      )
      if (!requiredTrackProgress?.userCompleted) {
        void navigate(`/training${onboardingSearch}`)
        return
      }
    }

    // Mark section as inProgress on visit
    if (!currentModule) return
    if (trainingModulesQuery.isLoading) return

    const modules = trainingModulesQuery.data?.modules ?? []
    const existing = modules.find((m) => m.module_id === currentModule.id)

    if (!existing) {
      updateModuleStatusMutation.mutate({
        moduleId: currentModule.id,
        data: { status: "inProgress", started_at: new Date().toISOString() },
      })
    }
  }, [
    trainingModulesQuery.data,
    trainingModulesQuery.isLoading,
    progressQuery.data,
    location.pathname,
    currentTrack,
    currentModule,
    onboardingSearch,
  ])

  return {
    isOnCoursePage,
    isCurrentModuleCompleted,
    handleNextClick,
    handleBackClick,
    canGoPrevious: !!getPreviousUrl(location.pathname),
    updateModuleStatusMutation,
    isOnLastPage,
    isOnQuizPage,
    currentTrack,
  }
}
